/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * A template literal tag function that removes a consistent amount of leading whitespace from multi-line strings while
 * preserving internal indentation.
 *
 * Note:
 * - The amount of leading whitespace is determined by establishing a baseline of leading whitespace characters before
 * the first non-whitespace character after the first newline.
 * - Trailing whitespaces after the final line are also removed.
 * - Nested `fmt` template literals are not well-supported. Use regular template literals instead.
 *
 * @param strings - The template string parts (automatically provided by template literal syntax)
 * @param substitutions - The interpolated values (automatically provided by template literal syntax)
 * @returns The formatted string with consistent leading whitespace removed and trimmed
 *
 * @example
 * ```typescript
 * const name = "Alice";
 * const message = fmt`
 *   Welcome ${name}!
 *     Please enjoy your stay.
 *   Have a great day!
 * `;
 * ```
 * Result:
 * ```
 * Welcome Alice!
 *   Please enjoy your stay.
 * Have a great day!
 * ```
 */
export function fmt(
  strings: {raw: readonly string[]},
  ...substitutions: any[]
) {
  const str = String.raw(strings, ...substitutions);

  const firstNewlineIndex = str.indexOf('\n');
  if (firstNewlineIndex === -1) {
    return str.trim(); // Not a multi-line string, so no need to dedent
  }

  // Count the number of spaces before the first non-whitespace character after the first newline. This column is
  // considered the baseline to align all other lines within the string.
  let count = 0;
  for (let i = firstNewlineIndex + 1; i < str.length; i += 1) {
    const char = str[i];
    if (char === '\n') {
      count = 0;
    } else if (char === ' ' || char === '\t') {
      count += 1;
    } else {
      break; // We've found the first non-whitespace character
    }
  }

  // Remove leading whitespace from each line
  const result = str
    .replace(/^([ \t\r\v]+)/gm, (_, whitespace) => {
      const maxSpaces = Math.max(whitespace.length - count, 0);
      return whitespace.slice(0, maxSpaces);
    })
    .trim();

  return result;
}
